import React, { useEffect, useMemo, useState } from 'react';
import '../../../../styles/ScheduleTable.css';
import '../../../../styles/Modal.css';
import { GameComponents } from './GameComponents.tsx';
import { TableComponents } from './TableComponents.tsx';
import { MobileColumnTab } from './mobileMarketColumns.ts';
import { useSport } from '../../../../context/SportContext.tsx';
import { Schedule } from '../../../../models/Schedule.tsx';
import AlertError from '../../../alerts/AlertError.tsx';
import { useModal } from '../../../../context/ModalContext.tsx';
import { useBetContext } from '../../../../context/BetContext.tsx';
import { BetDetails } from '../../../../models/MyBetModel.tsx';
import { openStraightBetSlipOnSelect } from '../../../../helpers/openBetSlipMode.tsx';
import { buildLeagueSections, ScheduleDateHeader } from '../../../../helpers/scheduleStickyHeaders.tsx';

function ScheduleTable() {
    const { activeGames, selectedGames } = useSport();
    const [, setShowLimitAlert] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loadingBet, setLoadingBet] = useState(false);
    const [pitchersChecked, setPitchersChecked] = useState<Record<string, { away: boolean; home: boolean }>>({});
    const { showModal, updateModalContent } = useModal();
    const { bets, setBets } = useBetContext();
    const [mobileColumnTab, setMobileColumnTab] = useState<MobileColumnTab>('spread');
    const [showMoneylineSlot, setShowMoneylineSlot] = useState(false);

    const handlePitcherChange = (gameId: string, team: "away" | "home") => {
        setPitchersChecked(prev => ({
            ...prev,
            [gameId]: {
                away: prev[gameId]?.away ?? false,
                home: prev[gameId]?.home ?? false,
                [team]: !(prev[gameId]?.[team] ?? false),
            },
        }));
    };

    useEffect(() => {
        setBets((prevBets: BetDetails[]) => {
            let changed = false;
            const next = prevBets.map((bet) => {
                if (bet.league !== "MLB" || !bet.gameId) return bet;

                const checked = pitchersChecked[bet.gameId];
                if (!checked) return bet;

                const away = checked.away ?? false;
                const home = checked.home ?? false;

                if (
                    bet.pitchers?.away === away &&
                    bet.pitchers?.home === home &&
                    bet.pitchers?.names
                ) {
                    return bet;
                }

                changed = true;
                return {
                    ...bet,
                    pitchers: {
                        away,
                        home,
                        names: {
                            away: bet.pitchers?.names?.away ?? "",
                            home: bet.pitchers?.names?.home ?? "",
                        },
                        ids: {
                            away: bet.pitchers?.ids?.away ?? "",
                            home: bet.pitchers?.ids?.home ?? "",
                        },
                    },
                };
            });
            return changed ? next : prevBets;
        });
    }, [pitchersChecked, setBets]);

    const handleSpreadTabClick = () => {
        if (window.innerWidth >= 992) return;

        if (mobileColumnTab === 'spread') {
            setShowMoneylineSlot(prev => !prev);
            return;
        }

        setMobileColumnTab('spread');
        setShowMoneylineSlot(false);
    };

    const handleTotalTabClick = () => {
        if (window.innerWidth >= 992) return;

        setMobileColumnTab(prev => (prev === 'teamTotal' ? 'spread' : 'teamTotal'));
        setShowMoneylineSlot(false);
    };

    const onShowBetsModal = (incomingBets?: BetDetails[]) => {
        openStraightBetSlipOnSelect({
            incomingBets,
            showModal,
            updateModalContent,
            setShowLimitAlert,
            setErrorMessage,
        });
    };

    const unplayedGames = useMemo(
        () => activeGames.filter((game: Schedule) => game.status === 'unplayed'),
        [activeGames]
    );

    const leagueSections = useMemo(
        () => buildLeagueSections(unplayedGames),
        [unplayedGames]
    );

    if (!selectedGames) return null;

    let rowIndex = 0;

    return (
        <>
            {unplayedGames.length > 0 && (
                <div className="containertable">
                    {leagueSections.map(league => {
                        const firstGame = league.dates[0].games[0];
                        return (
                            <div className="schedule-league-section" key={league.leagueKey}>
                                <TableComponents
                                    game={firstGame}
                                    showColumnHeaders={false}
                                />
                                <div className="schedule-sticky-columns">
                                    <TableComponents
                                        game={firstGame}
                                        showLeagueBanner={false}
                                        mobileColumnTab={mobileColumnTab}
                                        onSpreadTabClick={handleSpreadTabClick}
                                        onTotalTabClick={handleTotalTabClick}
                                    />
                                </div>
                                {league.dates.map(dateSection => (
                                    <div
                                        className="schedule-date-section"
                                        key={`${league.leagueKey}-${dateSection.dateKey}`}
                                    >
                                        <ScheduleDateHeader startDate={dateSection.games[0].start_date} />
                                        {dateSection.games.map(game => {
                                            const currentRow = rowIndex;
                                            rowIndex += 1;
                                            return (
                                                <GameComponents
                                                    key={`${game.leagueKey}-${game.game_id}`}
                                                    game={game}
                                                    onShowBetsModal={onShowBetsModal}
                                                    showHeaderByLeague={false}
                                                    showHeaderByDate={false}
                                                    pitchersChecked={
                                                        pitchersChecked[game.game_id] ?? {
                                                            away: false,
                                                            home: false,
                                                        }
                                                    }
                                                    onPitcherChange={handlePitcherChange}
                                                    setLoadingBet={setLoadingBet}
                                                    loadingBet={loadingBet}
                                                    rowIndex={currentRow}
                                                    setErrorMessage={setErrorMessage}
                                                    mobileColumnTab={mobileColumnTab}
                                                    showMoneylineSlot={showMoneylineSlot}
                                                    onMobileColumnTabChange={setMobileColumnTab}
                                                    onShowMoneylineSlotChange={setShowMoneylineSlot}
                                                />
                                            );
                                        })}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}

            {errorMessage && (
                <AlertError
                    message={errorMessage}
                    onClose={() => setErrorMessage('')}
                />
            )}
        </>
    );
}

export default ScheduleTable;
